import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const TRANSITION_MS = 300;

interface DrawerOverlayProps {
  children: ReactNode;
  onClose: () => void;
}

export function DrawerOverlay({ children, onClose }: DrawerOverlayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closingRef = useRef(false);

  useEffect(() => {
    requestAnimationFrame(() => setIsOpen(true));
  }, []);

  function handleOpenChange(open: boolean) {
    if (!open && !closingRef.current) {
      closingRef.current = true;
      setIsOpen(false);
      setTimeout(() => onClose(), TRANSITION_MS);
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal forceMount>
        <StyledOverlay />
        <StyledContent aria-label="Dutchie Agent Assist">{children}</StyledContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const slideUp = keyframes`
  from { transform: translate3d(0, 100%, 0); }
  to   { transform: translate3d(0, 0, 0); }
`;

const slideDown = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(0, 100%, 0); }
`;

const StyledOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(16, 48, 77, 0.5);

  &[data-state='open'] {
    animation: ${fadeIn} ${TRANSITION_MS}ms ease;
  }
  &[data-state='closed'] {
    animation: ${fadeOut} ${TRANSITION_MS}ms ease;
  }
`;

const StyledContent = styled(Dialog.Content)`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  height: 100%;
  max-height: calc(100% - 20px);
  border-radius: 20px 20px 0 0;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  & > * {
    flex: 1;
    min-height: 0;
  }

  &[data-state='open'] {
    animation: ${slideUp} ${TRANSITION_MS}ms ease;
  }
  &[data-state='closed'] {
    animation: ${slideDown} ${TRANSITION_MS}ms ease;
  }
`;
